import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { useFormikContext } from 'formik';

import { defaultErrorMessageSeparator } from 'src/hooks/useConvertCurrencyToCentsDebounced/useHandleMinMax';
import { getDescendantProp } from 'src/utils/utils';

enum ERROR_TYPES {
    FORM_ERROR = 'FORM_ERROR',
    INPUT_ERROR = 'INPUT_ERROR',
}

type UseHandleNPICurrencyInputFieldErrorInput = {
    fieldName: string;
    errorMessage?: string;
};

type UseHandleNPICurrencyInputFieldErrorOutput = {
    inputErrorMessages?: string;
    setInputErrorMessages: Dispatch<SetStateAction<string>>;
    finalErrorMessage: string[];
    hasError: boolean;
};

type ErrorMapByErrorTypes = Record<ERROR_TYPES, string> | object;

/**
 * Merges input errors, form errors and provided errors (via props) into a single interface.
 * We decouple `NPICurrencyInput` from a form library and `NPICurrencyInputField` serves as the adapter
 * - Updates form errors with input errors (min/max errors handled in NPICurrencyInput sent to Form Errors)
 * - - A map receives input errors via setInputErrorMessages and remains input error type until made a form error
 * - - Once the input error is registered in the form error, but removed from input errors map, it is also removed from form errors
 * - FinalErrorMessage merging display of errors
 * @param fieldName string Form Field Name
 * @param errorMessage string Passed in Error Message
 * @returns {Object} output UseHandleNPICurrencyInputFieldErrorOutput
 * @returns output.inputErrorMessages - The error messages as a single string separated by defaultErrorMessageSeparator from `NPICurrencyInput`;
 * @returns output.setInputErrorMessages - the setter that `NPICurrencyInput` uses to set Input Error Messages;
 * @returns output.finalErrorMessage - The merged errors from the form and the input to be used to display the end result;
 * @returns output.hasError - whether or not has an error (either form or input);
 */
function useHandleNPICurrencyInputFieldError({
    fieldName,
    errorMessage,
}: UseHandleNPICurrencyInputFieldErrorInput): UseHandleNPICurrencyInputFieldErrorOutput {
    const { setFieldError, errors: formikFormErrors, isValidating } = useFormikContext();

    /**
     * The error in the form context
     */
    const formFieldError = useMemo(() => {
        return getDescendantProp(formikFormErrors, fieldName);
    }, [formikFormErrors, fieldName]);

    const isFormFieldErrorSameAsErrorMessage = errorMessage === formFieldError;
    const [inputErrorMessages, setInputErrorMessages] = useState();
    const initialFinalErrorMessage = useMemo(() => {
        const isErrorMessageArray = Array.isArray(errorMessage);
        const isErrorMessageString = typeof errorMessage === 'string';

        if (!isErrorMessageArray && isErrorMessageString) {
            // multiple error messages in single string to parse
            return errorMessage
                ?.split(defaultErrorMessageSeparator)
                ?.filter((errorMessageString) => {
                    return errorMessageString !== '';
                });
        }

        if (isErrorMessageArray) {
            // already parsed
            return errorMessage as string[];
        }

        // empty initial errors
        return [];
    }, [errorMessage]);
    const [finalErrorMessage, setFinalErrorMessage] = useState(initialFinalErrorMessage);
    const [errorMapByErrorTypes, setErrorMapByErrorTypes] = useState<ErrorMapByErrorTypes>({});
    const [inputErrorToBeRemovedFromFormFieldError, setInputErrorToBeRemovedFromFormFieldError] =
        useState();

    const hasError = useMemo(() => {
        return finalErrorMessage?.length > 0;
    }, [finalErrorMessage]);

    const handleUpdateFormErrorMap = (
        formFieldError,
        errorMapByErrorTypes,
        setErrorMapByErrorTypes
    ) => {
        const newErrorMapByErrorTypes = {
            ...errorMapByErrorTypes,
            [ERROR_TYPES.FORM_ERROR]: formFieldError,
        };
        setErrorMapByErrorTypes(newErrorMapByErrorTypes);
    };

    const handleUpdateInputErrorMap = (inputErrorMessages, errorMapByErrorTypes) => {
        const newErrorMapByErrorTypes = {
            ...errorMapByErrorTypes,
            [ERROR_TYPES.INPUT_ERROR]: inputErrorMessages,
        };
        setErrorMapByErrorTypes(newErrorMapByErrorTypes);
        return { newErrorMapByErrorTypes, oldErrorMapByErrorTypes: errorMapByErrorTypes };
    };

    const handleSetInputErrorToBeRemovedFromFormFieldError = (
        newErrorMapByErrorTypes,
        oldErrorMapByErrorTypes
    ) => {
        const oldInputError = oldErrorMapByErrorTypes?.[ERROR_TYPES.INPUT_ERROR];

        const oldMapFormErrorIncludesOldInputError =
            oldErrorMapByErrorTypes[ERROR_TYPES.FORM_ERROR]?.includes(oldInputError);

        const newMapInputErrorRemovedOld =
            !newErrorMapByErrorTypes?.[ERROR_TYPES.INPUT_ERROR]?.includes(oldInputError);

        const shouldRemoveInputErrorFromFormError =
            oldMapFormErrorIncludesOldInputError && newMapInputErrorRemovedOld
                ? oldInputError
                : undefined;
        setInputErrorToBeRemovedFromFormFieldError(shouldRemoveInputErrorFromFormError);
    };

    useEffect(() => {
        // Keep form error map updated with formFieldErrors
        const shouldUpdateMapWithFormError =
            errorMapByErrorTypes[ERROR_TYPES.FORM_ERROR] !== formFieldError;
        if (shouldUpdateMapWithFormError) {
            handleUpdateFormErrorMap(formFieldError, errorMapByErrorTypes, setErrorMapByErrorTypes);
        }
    }, [errorMapByErrorTypes, formFieldError]);

    useEffect(() => {
        // Keep input error map updated with inputErrorMessages
        const shouldUpdateMapWithInputError =
            errorMapByErrorTypes[ERROR_TYPES.INPUT_ERROR] !== inputErrorMessages;
        if (shouldUpdateMapWithInputError) {
            const { newErrorMapByErrorTypes, oldErrorMapByErrorTypes } = handleUpdateInputErrorMap(
                inputErrorMessages,
                errorMapByErrorTypes
            );

            handleSetInputErrorToBeRemovedFromFormFieldError(
                newErrorMapByErrorTypes,
                oldErrorMapByErrorTypes
            );
        }
    }, [errorMapByErrorTypes, inputErrorMessages]);

    useEffect(() => {
        // Remove input errors from formField Error
        if (
            inputErrorToBeRemovedFromFormFieldError &&
            formFieldError?.includes(inputErrorToBeRemovedFromFormFieldError)
        ) {
            setFieldError(
                fieldName,
                formFieldError?.replace(inputErrorToBeRemovedFromFormFieldError, '') || undefined
            );
        }
    }, [fieldName, formFieldError, setFieldError, inputErrorToBeRemovedFromFormFieldError]);

    const getMergedAllErrorsToAdd = useCallback(
        (
            inputErrorsFromErrorMap,
            formErrorsFromErrorMap,
            isInitialErrorMessageSetInFormFieldError
        ) => {
            const isAllInputErrorsAddedToFormFieldError =
                inputErrorsFromErrorMap?.length > 0
                    ? inputErrorsFromErrorMap?.every((inputError) => {
                          return formFieldError?.includes(inputError);
                      })
                    : true;

            const formErrorsToAdd = formErrorsFromErrorMap ?? [];
            const inputErrorsToAdd = isAllInputErrorsAddedToFormFieldError
                ? []
                : inputErrorsFromErrorMap ?? [];

            const initialErrorToAdd = isInitialErrorMessageSetInFormFieldError
                ? []
                : initialFinalErrorMessage ?? [];

            const mergedAllErrorsToAdd = [
                ...formErrorsToAdd,
                ...inputErrorsToAdd,
                ...initialErrorToAdd,
            ]?.join(defaultErrorMessageSeparator);

            return mergedAllErrorsToAdd;
        },
        [formFieldError, initialFinalErrorMessage]
    );

    /**
     * Tracks the state and determines when to update which value based on the state
     ** When to update finalErrorMessage
     ** When to update the formFieldError
     */
    const checkShouldUpdateFinalErrorMessageOrSetFieldError = useCallback(() => {
        const formErrorsFromErrorMap = errorMapByErrorTypes[ERROR_TYPES.FORM_ERROR]?.split(
            defaultErrorMessageSeparator
        );
        const inputErrorsFromErrorMap = errorMapByErrorTypes[ERROR_TYPES.INPUT_ERROR]?.split(
            defaultErrorMessageSeparator
        );

        const isInitialErrorMessageSetInFormFieldError =
            initialFinalErrorMessage?.length > 0 && !isFormFieldErrorSameAsErrorMessage
                ? formFieldError?.includes(initialFinalErrorMessage) ?? false
                : true;

        const isAllInputErrorsAddedToFormErrorsFromErrorMap =
            inputErrorsFromErrorMap?.length > 0
                ? inputErrorsFromErrorMap?.every((inputError) => {
                      return formErrorsFromErrorMap?.includes(inputError);
                  })
                : true;

        const isCombineFormErrorsWithInputErrorsNotInFormErrorsOnly =
            formErrorsFromErrorMap &&
            inputErrorsFromErrorMap &&
            !isAllInputErrorsAddedToFormErrorsFromErrorMap &&
            isInitialErrorMessageSetInFormFieldError;

        const combinedFormErrorsWithInputErrorsNotInFormErrors = `${formErrorsFromErrorMap}${defaultErrorMessageSeparator}${inputErrorsFromErrorMap?.filter(
            (inputError) => {
                return !formErrorsFromErrorMap?.includes(inputError);
            }
        )}`;

        const currentFinalErrorMessageStringFromMap =
            isCombineFormErrorsWithInputErrorsNotInFormErrorsOnly
                ? combinedFormErrorsWithInputErrorsNotInFormErrors
                : getMergedAllErrorsToAdd(
                      inputErrorsFromErrorMap,
                      formErrorsFromErrorMap,
                      isInitialErrorMessageSetInFormFieldError
                  );

        const newErrorMessage = currentFinalErrorMessageStringFromMap
            ?.trim()
            ?.split(defaultErrorMessageSeparator)
            ?.filter((errorMessageString) => {
                return errorMessageString !== '';
            });

        const newErrorMessageIncludesInitialAlready = !initialFinalErrorMessage?.reduce(
            (prev, final) => {
                return final && newErrorMessage.includes(prev);
            },
            true
        );
        const newFinalErrorMessage =
            initialFinalErrorMessage?.length > 0 && !newErrorMessageIncludesInitialAlready
                ? newErrorMessage.concat(
                      `${defaultErrorMessageSeparator}${initialFinalErrorMessage}`
                  )
                : newErrorMessage;

        const isFinalErrorMessageAsExpected =
            JSON.stringify(finalErrorMessage) === JSON.stringify(newFinalErrorMessage);

        const isFormFieldErrorInSyncWithFormErrorsFromErrorMap =
            formFieldError === errorMapByErrorTypes[ERROR_TYPES.FORM_ERROR];

        const inputErrorsNotFormErrorsYet =
            !isAllInputErrorsAddedToFormErrorsFromErrorMap &&
            isFormFieldErrorInSyncWithFormErrorsFromErrorMap; // Input errors in error map have not yet been updated to be a form error

        const initialErrorsNotFormErrorsYet =
            !isInitialErrorMessageSetInFormFieldError &&
            isFormFieldErrorInSyncWithFormErrorsFromErrorMap;

        const finalErrorMessageNeedsToUpdate =
            !isFinalErrorMessageAsExpected && isFormFieldErrorInSyncWithFormErrorsFromErrorMap;

        const shouldUpdate =
            inputErrorsNotFormErrorsYet ||
            initialErrorsNotFormErrorsYet ||
            finalErrorMessageNeedsToUpdate;

        const shouldUpdateFinalErrorMessage =
            JSON.stringify(finalErrorMessage) !== JSON.stringify(newErrorMessage);

        const newFormError = newFinalErrorMessage?.join(defaultErrorMessageSeparator);

        const shouldUpdateFieldError = formFieldError !== newFormError;

        return {
            shouldUpdate,
            newFinalErrorMessage,
            shouldUpdateFinalErrorMessage,
            shouldUpdateFieldError,
            newFormError,
        };
    }, [
        errorMapByErrorTypes,
        finalErrorMessage,
        formFieldError,
        getMergedAllErrorsToAdd,
        initialFinalErrorMessage,
        isFormFieldErrorSameAsErrorMessage,
    ]);

    useEffect(() => {
        const {
            newFinalErrorMessage,
            shouldUpdate,
            newFormError,
            shouldUpdateFieldError,
            shouldUpdateFinalErrorMessage,
        } = checkShouldUpdateFinalErrorMessageOrSetFieldError();
        if (shouldUpdate) {
            if (shouldUpdateFinalErrorMessage) {
                setFinalErrorMessage(newFinalErrorMessage);
            }

            if (shouldUpdateFieldError) {
                setFieldError(fieldName, newFormError?.length > 0 ? newFormError : undefined);
            }
        }
    }, [
        checkShouldUpdateFinalErrorMessageOrSetFieldError,
        errorMapByErrorTypes,
        fieldName,
        finalErrorMessage,
        formFieldError,
        getMergedAllErrorsToAdd,
        initialFinalErrorMessage,
        isFormFieldErrorSameAsErrorMessage,
        isValidating,
        setFieldError,
    ]);

    return {
        inputErrorMessages,
        setInputErrorMessages,
        finalErrorMessage,
        hasError,
    };
}

export default useHandleNPICurrencyInputFieldError;
